import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { supabase } from '../../lib/supabase';
import { useToast } from '../ui/Toast';
import { AddressForm } from '../forms/AddressForm';
import { AddressData } from '../../lib/utils/geocoding';
import { useQueryClient } from '@tanstack/react-query';
import { devLog } from '../../utils/devLog';

interface AddressUpdateFlowProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function AddressUpdateFlow({ onClose, onSuccess }: AddressUpdateFlowProps) {
  const { data: companyId } = useCurrentCompany();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [addressData, setAddressData] = useState<AddressData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWebsitePrompt, setShowWebsitePrompt] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [_googleWebsiteUrl, setGoogleWebsiteUrl] = useState<string | null>(null);

  const handleAddressValidate = (isValid: boolean, data?: AddressData) => {
    if (isValid && data) {
      setAddressData(data);
    } else {
      setAddressData(null);
    }
  };

  const handleSubmit = async () => {
    if (!companyId || !addressData?.place_id) {
      showToast('Please search and select a business address', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      // Delete old google_business_summary and google_reviews records for this company (company-level, location_id = null)
      // This ensures we don't have orphaned records when place_id changes
      await (supabase as any)
        .from('google_business_summary')
        .delete()
        .eq('company_id', companyId)
        .is('location_id', null);
      
      await (supabase as any)
        .from('google_reviews')
        .delete()
        .eq('company_id', companyId)
        .is('location_id', null);

      // Update company with place_id
      const { error: updateError } = await (supabase as any)
        .from('companies')
        .update({ place_id: addressData.place_id } as any)
        .eq('id', companyId);

      if (updateError) {
        // If column doesn't exist (migration not applied), show error
        if (updateError.code === 'PGRST116' || updateError.message?.includes('does not exist') || updateError.message?.includes('column')) {
          showToast('place_id column not available. Migrations may not be applied.', 'error');
          return;
        }
        throw updateError;
      }

      // Track if edge function succeeded for query invalidation
      let edgeFunctionSucceeded = false;

      // Trigger Edge Function to fetch Google Business summary
      try {
      // Get session token for authenticated edge function call
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('Authentication required. Please log in again.');
      }

      // Use fetch directly to ensure auth header is properly sent
      const functionUrl = `${process.env.REACT_APP_SUPABASE_URL || 'http://127.0.0.1:54321'}/functions/v1/fetch-google-business-summary`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          place_id: addressData.place_id,
          company_id: companyId,
          location_id: null, // Can be null for company-level summaries
        }),
      });

      let fetchData: any = null;
      let fetchError: any = null;

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Unknown error';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorText;
        } catch {
          errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`;
        }
        
        fetchError = {
          message: errorMessage,
          status: response.status,
        };
        
        console.error('Error fetching Google Business summary:', fetchError);
        // Show detailed error message
        const isTableNotFound = errorMessage.includes('does not exist') || errorMessage.includes('relation') || errorMessage.includes('table');
        if (isTableNotFound) {
          showToast('Address updated, but database table not found. Migrations may not be applied.', 'error');
        } else if (response.status === 401) {
          showToast('Authentication failed. Please log out and log back in.', 'error');
        } else {
          showToast(`Profile linked, but failed to fetch business details: ${errorMessage}`, 'error');
        }
        // Don't invalidate queries - edge function failed
      } else {
        fetchData = await response.json();
        
        if (fetchData?.success) {
          devLog('Successfully fetched Google Business summary:', fetchData);
          edgeFunctionSucceeded = true;
          const googleWebsite = fetchData.summary?.website_url;
          setGoogleWebsiteUrl(googleWebsite || null);
          
          // Check if company already has website_url
          const { data: companyData } = await (supabase as any)
            .from('companies')
            .select('website_url')
            .eq('id', companyId)
            .single();
          
          // If Google provided website and company doesn't have one, update it
          if (googleWebsite && !companyData?.website_url) {
            await (supabase as any)
              .from('companies')
              .update({ website_url: googleWebsite })
              .eq('id', companyId);
            // Invalidate website assessment query
            await queryClient.invalidateQueries({ queryKey: ['website-assessment', companyId] });
          } else if (!googleWebsite && !companyData?.website_url) {
            // Google didn't provide website and company doesn't have one - prompt user
            setShowWebsitePrompt(true);
            // Still invalidate queries since edge function succeeded
            await queryClient.invalidateQueries({ queryKey: ['google-business-summary', companyId] });
            await queryClient.invalidateQueries({ queryKey: ['google-reviews', companyId] });
            return; // Don't close modal yet, wait for website input
          }
        } else if (fetchData?.error) {
          console.error('Edge Function returned error:', fetchData.error);
          const errorMessage = typeof fetchData.error === 'string' ? fetchData.error : fetchData.error?.message || 'Unknown error';
          const isTableNotFound = errorMessage.includes('does not exist') || errorMessage.includes('relation') || errorMessage.includes('table');
          if (isTableNotFound) {
            showToast('Address updated, but database table not found. Migrations may not be applied.', 'error');
          } else {
            showToast(`Failed to fetch business details: ${errorMessage}`, 'error');
          }
          // Don't invalidate queries - edge function failed
        }
      }
    } catch (fetchErr) {
        console.error('Error calling fetch-google-business-summary:', fetchErr);
        const errorMessage = fetchErr instanceof Error ? fetchErr.message : 'Unknown error';
        const isTableNotFound = errorMessage.includes('does not exist') || errorMessage.includes('relation') || errorMessage.includes('table');
        if (isTableNotFound) {
          showToast('Address updated, but database table not found. Migrations may not be applied.', 'error');
        } else {
          showToast(`Failed to fetch business details: ${errorMessage}`, 'error');
        }
        // Don't invalidate queries - edge function failed
      }

      // Only invalidate React Query cache if edge function succeeded
      // This prevents showing "Fetch Business Details" screen when fetch failed
      if (edgeFunctionSucceeded) {
        // Small delay to ensure database write has completed
        await new Promise(resolve => setTimeout(resolve, 500));
        await queryClient.invalidateQueries({ queryKey: ['google-business-summary', companyId] });
        await queryClient.invalidateQueries({ queryKey: ['google-reviews', companyId] });
      }

      if (!showWebsitePrompt) {
        if (edgeFunctionSucceeded) {
          showToast('Google Business Profile linked successfully!', 'success');
        } else {
          showToast('Address updated, but failed to fetch business details. You can try refreshing manually.', 'info');
        }
        onSuccess();
      }
    } catch (error) {
      console.error('Error updating place_id:', error);
      showToast('Failed to link Google Business Profile', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWebsiteSubmit = async () => {
    if (!companyId || !websiteUrl.trim()) {
      showToast('Please enter a valid website URL', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      // Normalize URL (ensure it starts with http:// or https://)
      let normalizedUrl = websiteUrl.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = 'https://' + normalizedUrl;
      }

      const { error: updateError } = await (supabase as any)
        .from('companies')
        .update({ website_url: normalizedUrl })
        .eq('id', companyId);

      if (updateError) {
        throw updateError;
      }

      // Invalidate website assessment query
      await queryClient.invalidateQueries({ queryKey: ['website-assessment', companyId] });

      showToast('Website URL saved successfully!', 'success');
      setShowWebsitePrompt(false);
      onSuccess();
    } catch (error) {
      console.error('Error updating website URL:', error);
      showToast('Failed to save website URL', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg max-w-2xl w-full p-6 my-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-text-primary">
            {showWebsitePrompt ? 'Add Website URL' : 'Link Google Business Profile'}
          </h3>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {showWebsitePrompt ? (
          <div className="space-y-6">
            <div>
              <p className="text-sm text-text-secondary mb-4">
                We couldn&apos;t find a website URL for your business on Google. Please enter your website URL to enable website assessment.
              </p>
              <input
                type="text"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://example.com"
                maxLength={2048}
                className="w-full px-4 py-2 bg-[#363232] border border-[#504A4A] rounded-lg text-text-primary placeholder-[#706A6A] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                disabled={isSubmitting}
              />
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t border-[#504A4A]">
              <Button
                onClick={() => {
                  setShowWebsitePrompt(false);
                  onSuccess(); // Allow skipping website
                }}
                className="bg-[#363232] hover:bg-[#504A4A] text-text-primary"
                disabled={isSubmitting}
              >
                Skip
              </Button>
              <Button
                onClick={handleWebsiteSubmit}
                disabled={isSubmitting || !websiteUrl.trim()}
                className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
              >
                {isSubmitting ? 'Saving...' : 'Save Website'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <p className="text-sm text-text-secondary mb-4">
                Search for your business by name or address. Once selected, we&apos;ll automatically link your Google Business Profile.
              </p>
              <AddressForm
                onValidate={handleAddressValidate}
                hideTitle
                placeholder="Enter address or business name..."
                requiredFields={['street', 'city', 'state', 'zip']}
              />
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t border-[#504A4A]">
              <Button
                onClick={onClose}
                className="bg-[#363232] hover:bg-[#504A4A] text-text-primary"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !addressData?.place_id}
                className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
              >
                {isSubmitting ? 'Linking...' : 'Link Profile'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
